import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Coffee, Play, LogOut, LogIn, Clock, AlertTriangle, CheckCircle2, X, Moon, CalendarDays } from "lucide-react";
import { useAttendance } from "@/hooks/useAttendance";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/lang";

function formatMinutes(mins) {
  if (!mins || mins <= 0) return "0m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function CaptainAttendanceWidget({ token, compact = false }) {
  const { status, failed, acting, refresh, startBreak, endBreak, dutyOut, dutyIn, resume } = useAttendance(token);
  const [open, setOpen] = useState(false);
  const [confirmDutyOut, setConfirmDutyOut] = useState(false);

  // Someone who skips attendance (attendance.exempt) has nothing to show.
  if (status?.exempt) return null;
  if (!status) {
    // Unknown is said, in red, and one tap asks again.
    if (!failed) return null;
    return (
      <button onClick={() => refresh()} data-testid="attendance-status-pill"
        className="flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold border bg-card text-danger border-danger/50">
        <AlertTriangle className="w-4 h-4" />
        <span>{t("हाज़िरी नहीं दिखी · Retry")}</span>
      </button>
    );
  }

  const state = status.state || "NOT_CHECKED_IN";
  const isOnDuty = state === "ON_DUTY";
  const isOnBreak = state === "ON_BREAK";
  const isGap = state === "ON_SPLIT_GAP";
  const isAttention = state === "ATTENDANCE_EXCEPTION";
  const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

  // Pill styling based on state
  let pillColor = "bg-card text-muted-foreground border-border";
  let dotColor = "bg-muted-foreground";
  let label = t("ड्यूटी बंद · Off duty");
  let timeStr = "";

  if (isOnDuty) {
    pillColor = "bg-card text-foreground border-success/40";
    dotColor = "bg-success animate-pulse";
    label = t("ड्यूटी पर · On duty");
    timeStr = formatMinutes(status.elapsed_minutes);
  } else if (isOnBreak) {
    pillColor = "bg-card text-foreground border-warning/50";
    dotColor = "bg-warning animate-pulse";
    label = t("ब्रेक · Break");
    timeStr = formatMinutes(status.active_break_minutes);
  } else if (isGap) {
    pillColor = "bg-card text-foreground border-info/50";
    dotColor = "bg-info";
    label = t("स्प्लिट ब्रेक · Split break");
    timeStr = status.resume?.due_at ? `→ ${clock(status.resume.due_at)}` : "";
  } else if (isAttention) {
    pillColor = "bg-card text-danger border-danger/50";
    dotColor = "bg-danger";
    label = t("ध्यान दें · Check");
  }

  return (
    <>
      {/* COMPACT ATTENDANCE STATUS PILL */}
      <button
        onClick={() => {
          setConfirmDutyOut(false);
          setOpen(true);
        }}
        data-testid="attendance-status-pill"
        className={`flex items-center gap-1.5 ${
          compact
            ? "h-8 px-2 text-[11px] max-w-[7.5rem]"
            : "h-8 sm:h-9 px-2 sm:px-2.5 text-[11px] sm:text-xs max-w-[8.5rem] sm:max-w-[11rem]"
        } rounded-full font-semibold border whitespace-nowrap active:scale-95 ${pillColor}`}
        title={label}
      >
        <span className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full shrink-0 ${dotColor}`} />
        <span className="truncate">{label}</span>
        {timeStr && <span className="opacity-80 font-normal tabular-nums">{timeStr}</span>}
      </button>

      {/* QUICK ATTENDANCE DIALOG. Portalled to <body>: inside the CaptainPad top
          bar a fixed overlay was trapped in the bar's box and never showed. */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 animate-in fade-in duration-150"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="relative w-full sm:max-w-sm bg-card border-t sm:border rounded-t-2xl sm:rounded-2xl shadow-2xl p-4 sm:p-5 space-y-3.5 sm:space-y-4 text-card-foreground max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-0 duration-200">
            <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-1 sm:hidden" />
            {/* Header */}
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                  <span>{t("हाज़िरी · Attendance")}</span>
                </h3>
                <p className="text-xs text-muted-foreground">
                  {status.employee_name || "Staff"} · {status.employee_id}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted text-muted-foreground"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Shift & Duration Info Card */}
            <div className="bg-muted/40 rounded-xl p-3.5 space-y-2.5 border text-xs">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t("शिफ्ट · Shift")}:</span>
                <span className="font-semibold text-foreground">
                  {status.shift_name} ({status.shift_timing || "Standard"})
                </span>
              </div>

              {isGap && (
                <div className="flex items-center gap-1.5 text-info font-medium bg-info/10 px-2.5 py-1.5 rounded-md" data-testid="attendance-gap-note">
                  <Moon className="w-4 h-4 shrink-0" />
                  <span>{t(`स्प्लिट ब्रेक, ${clock(status.resume?.due_at)} पर वापस · Split break, back at ${clock(status.resume?.due_at)}`)}</span>
                </div>
              )}
              {status.day_closed?.my_duty_out_at && state === "NOT_CHECKED_IN" && (
                <div className="flex items-center gap-1.5 text-muted-foreground font-medium bg-muted px-2.5 py-1.5 rounded-md" data-testid="attendance-day-closed">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>{t(`दिन बंद: ड्यूटी ${clock(status.day_closed.my_duty_out_at)} पर खत्म · Day closed: duty ended at ${clock(status.day_closed.my_duty_out_at)}`)}</span>
                </div>
              )}
              {status.needs_review && (
                <div className="flex items-center gap-1.5 text-warning font-medium bg-warning/10 px-2.5 py-1.5 rounded-md" data-testid="attendance-needs-review">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{t("पिछली ड्यूटी मैनेजर देखेंगे · Your last duty had no duty-out; the manager will fix it")}</span>
                </div>
              )}

              {status.late_minutes > 0 && (
                <div className="flex items-center gap-1.5 text-warning font-medium bg-warning/10 px-2.5 py-1.5 rounded-md">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{t(`${status.late_minutes} मिनट देर से आए · ${status.late_minutes} min late`)}</span>
                </div>
              )}

              {status.early_arrival_minutes > 0 && status.late_minutes === 0 && (
                <div className="flex items-center gap-1.5 text-success font-medium bg-success/10 px-2.5 py-1.5 rounded-md">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>{t("समय से पहले आगमन · Early Arrival")}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 pt-1 border-t">
                <div className="bg-background/80 p-2.5 rounded-lg border">
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-0.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{t("कार्य अवधि · Duty")}</span>
                  </div>
                  <strong className="text-sm font-bold text-foreground">
                    {formatMinutes(status.elapsed_minutes)}
                  </strong>
                </div>

                <div className="bg-background/80 p-2.5 rounded-lg border">
                  <div className="flex items-center gap-1.5 text-muted-foreground mb-0.5">
                    <Coffee className="w-3.5 h-3.5" />
                    <span>{t("कुल ब्रेक · Break")}</span>
                  </div>
                  <strong className="text-sm font-bold text-foreground">
                    {formatMinutes(
                      isOnBreak
                        ? status.total_break_minutes + status.active_break_minutes
                        : status.total_break_minutes
                    )}
                  </strong>
                </div>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="space-y-2.5 pt-1">
              {/* Off duty: one button starts it (after a duty-out, or when
                  the duty-in at sign-in did not happen). */}
              {status.can_resume && (
                <Button
                  onClick={() => resume()}
                  disabled={acting}
                  variant="action"
                  data-testid="attendance-resume-btn"
                  className="w-full h-12 rounded-lg text-base font-bold flex items-center justify-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  <span>{t("ड्यूटी शुरू करें · START DUTY")}</span>
                </Button>
              )}
              {status.can_duty_in && (
                <Button
                  onClick={() => dutyIn()}
                  disabled={acting}
                  variant="action"
                  data-testid="attendance-duty-in-btn"
                  className="w-full h-10 sm:h-11 rounded-lg text-sm font-bold flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>{t("ड्यूटी शुरू करें · DUTY IN")}</span>
                </Button>
              )}
              {/* COMPACT BREAK / END BREAK BUTTON */}
              {isOnDuty && (
                <Button
                  onClick={async () => {
                    await startBreak();
                  }}
                  disabled={acting}
                  data-testid="attendance-start-break-btn"
                  className="w-full h-10 sm:h-11 rounded-lg text-sm font-bold bg-warning hover:bg-warning/90 text-white shadow-xs flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                >
                  <Coffee className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>{t("ब्रेक शुरू करें · START BREAK")}</span>
                </Button>
              )}

              {isOnBreak && (
                <Button
                  onClick={async () => {
                    await endBreak();
                  }}
                  disabled={acting}
                  data-testid="attendance-end-break-btn"
                  className="w-full h-10 sm:h-11 rounded-lg text-sm font-bold bg-success hover:bg-success/90 text-white shadow-xs flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                >
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                  <span>{t("ब्रेक समाप्त करें · END BREAK")}</span>
                </Button>
              )}

              {/* DUTY OUT BUTTON */}
              {isOnBreak && (
                <p className="text-xs text-center text-muted-foreground" data-testid="attendance-end-break-first">
                  {t("ड्यूटी खत्म करने से पहले ब्रेक खत्म करें · End the break before duty out")}
                </p>
              )}
              {isAttention && (
                <p className="text-xs text-center text-danger" data-testid="attendance-flagged">
                  {t("मैनेजर इस रिकॉर्ड को देखेंगे · The manager will check this record.")}
                </p>
              )}
              {status.can_duty_out && !confirmDutyOut && (
                <Button
                  onClick={() => setConfirmDutyOut(true)}
                  disabled={acting}
                  variant="outline"
                  data-testid="attendance-duty-out-trigger-btn"
                  className="w-full h-9 rounded-lg text-xs font-semibold border-danger/30 text-danger hover:bg-danger/10 flex items-center justify-center gap-1.5"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>{t("ड्यूटी समाप्त करें · DUTY OUT")}</span>
                </Button>
              )}

              <Link
                to="/my-attendance"
                onClick={() => setOpen(false)}
                data-testid="attendance-my-days-link"
                className="w-full h-11 rounded-lg text-sm font-semibold border flex items-center justify-center gap-2 hover:bg-muted"
              >
                <CalendarDays className="w-4 h-4" />
                <span>{t("मेरी हाज़िरी · My attendance")}</span>
              </Link>

              {/* DUTY OUT INLINE CONFIRMATION */}
              {confirmDutyOut && (
                <div className="bg-danger/10 border border-danger/30 rounded-xl p-3 space-y-2 text-center animate-in zoom-in-95 duration-100">
                  <p className="text-xs font-semibold text-danger">
                    {t("क्या आप निश्चित रूप से ड्यूटी समाप्त करना चाहते हैं? · Confirm ending your shift?")}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setConfirmDutyOut(false)}
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-xs"
                    >
                      {t("रद्द करें · Cancel")}
                    </Button>
                    <Button
                      onClick={async () => {
                        await dutyOut();
                        setConfirmDutyOut(false);
                        setOpen(false);
                      }}
                      disabled={acting}
                      data-testid="attendance-confirm-duty-out-btn"
                      variant="destructive"
                      size="sm"
                      className="flex-1 text-xs font-bold"
                    >
                      {t("हाँ, समाप्त करें · Duty Out")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      , document.body)}
    </>
  );
}
